import { createTheme, DEFAULT_THEME, mergeMantineTheme, rem } from '@mantine/core';

const themeOverride = createTheme({
  // ─── Charte graphique MAS MAYA ───────────────────────────────────────────────
  colors: {
    // Brun MAS MAYA — couleur primaire #773F26
    brown: [
      '#FCF7F4', // 0 — blanc chaud
      '#F5E8DF', // 1 — blush très léger
      '#EDD2BE', // 2 — pêche pâle
      '#E0B89B', // 3 — tan clair
      '#CF9D78', // 4 — tan
      '#B87F56', // 5 — brun moyen
      '#773F26', // 6 ← BRAND PRIMARY
      '#5C2F1C', // 7 — brun sombre
      '#421F14', // 8 — très sombre
      '#2B120C', // 9 — quasi noir chaud
    ],

    // Beige chaud — fond secondaire #E4DDD1
    beige: [
      '#FDFCFB', // 0 — blanc cassé
      '#F8F4EF', // 1 — beige très clair
      '#F0EAE0', // 2 — beige léger
      '#E4DDD1', // 3 ← BRAND SECONDARY
      '#D6CDBE', // 4 — sable
      '#C7BAA8', // 5 — sable foncé
      '#B3A390', // 6 — taupe
      '#9D8B75', // 7 — taupe sombre
      '#85735C', // 8 — brun taupe
      '#6C5A47', // 9 — brun profond
    ],

    // Vert profond — accent discret / support sombre #2C504D
    forest: [
      '#F1F6F5', // 0 — vert très pâle
      '#E0EDEB', // 1
      '#C8DDD9', // 2
      '#AECCC7', // 3
      '#8FB6B1', // 4
      '#6D9E98', // 5
      '#4C837C', // 6
      '#2C504D', // 7 ← BRAND SUPPORT SOMBRE
      '#1F3B38', // 8 — vert très sombre
      '#142725', // 9 — quasi noir vert
    ],

    // Charcoal — typographie et neutres
    charcoal: [
      '#F4F3F1', // 0
      '#E4E2DD', // 1
      '#C9C5BD', // 2
      '#AEA99E', // 3
      '#948E83', // 4
      '#79736A', // 5
      '#5E5951', // 6
      '#443F39', // 7
      '#2B2720', // 8
      '#1E1C18', // 9 ← sidebar · texte principal
    ],
  },

  // ─── Couleur primaire : brun MAS MAYA ────────────────────────────────────────
  primaryColor: 'brown',
  primaryShade: { light: 6, dark: 5 },

  // ─── Typographie ─────────────────────────────────────────────────────────────
  fontFamily: '"Nunito", system-ui, -apple-system, sans-serif',
  headings: {
    fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
    fontWeight: '400',
    sizes: {
      h1: { fontSize: rem(52), lineHeight: '1.06' },
      h2: { fontSize: rem(36), lineHeight: '1.1' },
      h3: { fontSize: rem(24), lineHeight: '1.2' },
      h4: { fontSize: rem(18), lineHeight: '1.3' },
      h5: { fontSize: rem(15), lineHeight: '1.4' },
      h6: { fontSize: rem(13), lineHeight: '1.4' },
    },
  },

  fontSizes: {
    xs: rem(11),
    sm: rem(13),
    md: rem(15),
    lg: rem(17),
    xl: rem(20),
  },

  spacing: {
    xs: rem(6),
    sm: rem(10),
    md: rem(18),
    lg: rem(28),
    xl: rem(48),
  },

  lineHeights: {
    xs: '1.4',
    sm: '1.55',
    md: '1.65',
    lg: '1.75',
    xl: '1.85',
  },

  defaultRadius: 'xs',

  // ─── Composants ──────────────────────────────────────────────────────────────
  components: {
    // Bouton principal : fond brun #773F26, texte blanc, hover plus sombre
    Button: {
      defaultProps: {
        size: 'sm',
        radius: 'xs',
        color: 'brown',
      },
      styles: {
        root: {
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          fontFamily: '"Nunito", system-ui, sans-serif',
          fontSize: rem(11),
        },
      },
    },

    ActionIcon: {
      defaultProps: { radius: 'xs', size: 'sm' },
    },

    Group: {
      defaultProps: { wrap: 'nowrap', gap: 'md' },
    },

    Tabs: {
      defaultProps: { color: 'charcoal.9' },
    },

    Skeleton: {
      defaultProps: { h: '100%' },
    },

    Modal: {
      defaultProps: {
        closeOnClickOutside: false,
        radius: 20,
        overlayProps: { backgroundOpacity: 0.2, blur: 3 },
      },
      styles: {
        content: { backgroundColor: 'var(--mantine-color-beige-1)' },
        header: {
          backgroundColor: 'var(--mantine-color-beige-1)',
          borderTopLeftRadius: rem(20),
          borderTopRightRadius: rem(20),
          borderBottom: '1px solid var(--mantine-color-beige-3)',
          paddingBottom: rem(12),
          marginBottom: rem(4),
        },
        title: {
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: rem(22),
          fontWeight: 400,
          letterSpacing: '0.01em',
          color: 'var(--mantine-color-charcoal-9)',
        },
      },
    },

    // Cards sobres : bordures fines, fond blanc/beige très clair
    Paper: {
      defaultProps: {
        p: 'lg',
        withBorder: true,
        radius: 'xs',
        bg: 'white',
      },
      styles: {
        root: {
          borderColor: 'var(--mantine-color-beige-3)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        },
      },
    },

    Card: {
      defaultProps: {
        radius: 'xs',
        withBorder: true,
        bg: 'white',
      },
      styles: {
        root: {
          borderColor: 'var(--mantine-color-beige-3)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        },
      },
    },

    Stack: {
      defaultProps: { gap: 'md' },
    },

    // Tables lisibles, lignes aérées, peu de bordures lourdes
    Table: {
      defaultProps: {
        highlightOnHover: true,
        withTableBorder: true,
        withColumnBorders: false,
        verticalSpacing: 'sm',
        horizontalSpacing: 'lg',
      },
      styles: {
        table: { backgroundColor: 'var(--mantine-color-white)' },
        thead: { backgroundColor: 'var(--mantine-color-beige-2)' },
        tr: { borderColor: 'var(--mantine-color-beige-3)' },
        th: {
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontSize: rem(10),
          fontWeight: 700,
          color: 'var(--mantine-color-charcoal-5)',
          paddingTop: rem(10),
          paddingBottom: rem(10),
        },
        td: {
          fontSize: rem(14),
          color: 'var(--mantine-color-charcoal-8)',
        },
      },
    },

    // Badges : tons terreux, jamais flashy
    Badge: {
      defaultProps: { radius: 'xs', variant: 'light', size: 'sm' },
      styles: {
        root: {
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          fontSize: rem(9),
        },
      },
    },

    // Inputs : bordure fine beige, focus brun discret
    TextInput: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    Textarea: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    NumberInput: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    Select: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    MultiSelect: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    DateInput: {
      defaultProps: { radius: 'xs' },
      styles: {
        input: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-4)',
        },
        label: {
          letterSpacing: '0.05em',
          fontSize: rem(11),
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--mantine-color-charcoal-6)',
          marginBottom: rem(4),
        },
      },
    },

    Checkbox: {
      defaultProps: { radius: 'xs', color: 'brown' },
    },

    Switch: {
      defaultProps: { color: 'brown' },
      styles: {
        label: {
          fontSize: rem(12),
          color: 'var(--mantine-color-charcoal-7)',
        },
      },
    },

    Tooltip: {
      defaultProps: {
        radius: 'xs',
        color: 'charcoal.9',
        withArrow: false,
      },
      styles: {
        tooltip: {
          fontSize: rem(11),
          letterSpacing: '0.04em',
          backgroundColor: 'var(--mantine-color-charcoal-9)',
        },
      },
    },

    NavLink: {
      defaultProps: { radius: 'xs' },
      styles: {
        root: {
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          fontSize: rem(11),
          fontWeight: 600,
          borderRadius: rem(3),
          paddingTop: rem(9),
          paddingBottom: rem(9),
        },
        label: { fontFamily: '"Nunito", system-ui, sans-serif' },
      },
    },

    Title: {
      styles: {
        root: {
          fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
          fontWeight: 400,
          letterSpacing: '-0.01em',
          color: 'var(--mantine-color-charcoal-9)',
        },
      },
    },

    Divider: {
      styles: {
        root: { borderColor: 'var(--mantine-color-beige-3)' },
      },
    },

    Combobox: {
      defaultProps: { radius: 'xs' },
      styles: {
        dropdown: {
          backgroundColor: 'var(--mantine-color-white)',
          borderColor: 'var(--mantine-color-beige-3)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        },
        option: { fontSize: rem(13) },
      },
    },

    Avatar: {
      defaultProps: { radius: 'xs', color: 'brown' },
    },

    Loader: {
      defaultProps: { color: 'brown.6', size: 'sm' },
    },

    Notification: {
      styles: {
        root: {
          borderColor: 'var(--mantine-color-beige-3)',
          backgroundColor: 'var(--mantine-color-white)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

export const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride);
